import { URLS } from "@/base/utils/urls";
import { Button } from "@/flokit/core/button/button";
import { openInNewTabHtmlAttributes } from "@/flokit/core/button/link-button";
import { FirmProfileLinkInfoFragment } from "modules/graphql.types";
import Link from "next/link";
import React from "react";
import { FiLayout } from "react-icons/fi";

export const DATA_FEATURE_PROPERTIES = {
  activateAccountEvent: () => ({ "data-feature": "activate-account-event" }),
  activateAccountGeneral: () => ({
    "data-feature": "activate-account-general",
  }),
  quickApplyButton: (args: {
    orgId: string;
    orgName: string;
    jobId: string;
    jobName: string;
    source: ForwardJobApplicationSource;
    /**
     * If the element appears in a list, its index in that list.
     */
    index?: number;
  }) => ({
    "data-property-source": args.source,
    "data-feature": "quick apply",
    "data-property-org-id": args.orgId,
    "data-property-org-name": args.orgName,
    "data-property-job-id": args.jobId,
    "data-property-job-name": args.jobName,
    "data-property-index": args.index,
  }),
  applyOnFirmWebsite: (args: {
    orgId: string;
    orgName: string;
    jobId: string;
    jobName: string;
    /**
     * If the element appears in a list, its index in that list.
     */
    index?: number;
  }) => ({
    "data-feature": "apply on firm website",
    "data-property-org-id": args.orgId,
    "data-property-org-name": args.orgName,
    "data-property-job-id": args.jobId,
    "data-property-job-name": args.jobName,
    "data-property-index": args.index,
  }),
  firmProfileIngress: (args: {
    source: FirmProfileIngressSource;
    orgId: string;
    orgName: string;
    /**
     * If the element appears in a list, its index in that list. Not necessary if the index isn't too relevant to analytics, e.g. its in a user's dashboard rather than the search results on the job board.
     */
    index?: number;
  }) => ({
    "data-feature": "firm profile ingress",
    "data-property-source": args.source,
    "data-property-org-id": args.orgId,
    "data-property-org-name": args.orgName,
    "data-property-index": args.index,
  }),
  firmProfileViewDetailedBreakdownType: (args: {
    type: FirmProfileViewDetailedBreakdownType;
    orgId: string;
    orgName: string;
  }) => ({
    "data-feature": "firm profile view detailed breakdown",
    "data-property-type": args.type,
    "data-property-org-id": args.orgId,
    "data-property-org-name": args.orgName,
  }),
  visitFirmWebsite: (args: {
    source: VisitFirmWebsiteSource;
    orgId: string;
    orgName: string;
  }) => ({
    "data-feature": "visit firm website link",
    "data-property-source": args.source,
    "data-property-org-id": args.orgId,
    "data-property-org-name": args.orgName,
  }),
  firmDirectoryIngress: (args: { source: FirmDirectoryIngressSource }) => ({
    "data-feature": "firm directory ingress",
    "data-property-source": args.source,
  }),
  forwardPublishEvent: (args: {
    source: ForwardPublishEventSource;
    orgId: string;
    orgName: string;
    eventId: string;
    eventName: string;
  }) => ({
    "data-feature": "forward publish event",
    "data-property-source": args.source,
    "data-property-org-id": args.orgId,
    "data-property-org-name": args.orgName,
    "data-property-event-id": args.eventId,
    "data-property-event-name": args.eventName,
  }),
  forwardUnpublishEvent: (args: {
    source: ForwardUnpublishEventSource;
    orgId: string;
    orgName: string;
    eventId: string;
    eventName: string;
  }) => ({
    "data-feature": "forward unpublish event",
    "data-property-source": args.source,
    "data-property-org-id": args.orgId,
    "data-property-org-name": args.orgName,
    "data-property-event-id": args.eventId,
    "data-property-event-name": args.eventName,
  }),
  startRegistrationForFirmEventInForward: (args: {
    source: StartRegisterForFirmEventInForwardSource;
    orgId: string;
    eventId: string;
    alreadyRegistered: boolean;
    /**
     * The Event may be featured, but the candidate may not be clicking on the Register button in a context where that is relevant. For example, on Carousel or Detail Page, it doesn't matter whether the Event is featured.
     */
    showsFeatured: boolean;
  }) => ({
    "data-feature": "start registration for firm event in forward",
    "data-property-source": args.source,
    "data-property-org-id": args.orgId,
    "data-property-event-id": args.eventId,
    "data-property-already-registered": args.alreadyRegistered,
    "data-property-shows-featured": args.showsFeatured,
  }),
  completeRegistrationForFirmEventInForward: (args: {
    orgId: string;
    orgName: string;
    eventId: string;
    eventName: string;
    alreadyRegistered: boolean;
  }) => ({
    "data-feature": "complete registration for firm event in forward",
    "data-property-org-id": args.orgId,
    "data-property-org-name": args.orgName,
    "data-property-event-id": args.eventId,
    "data-property-event-name": args.eventName,
    "data-property-already-registered": args.alreadyRegistered,
  }),
  firmEventDetailPageIngress: (args: {
    source: FirmEventDetailPageIngressSource;
    orgId: string;
    eventId: string;
    showsFeatured: boolean;
    index?: number;
  }) => ({
    "data-feature": "firm event detail page ingress",
    "data-property-source": args.source,
    "data-property-org-id": args.orgId,
    "data-property-event-id": args.eventId,
    "data-property-shows-featured": args.showsFeatured,
    "data-property-index": args.index,
  }),
  submitJobApplicationInForward: (args: {
    source: ForwardJobApplicationSource;
    orgId: string;
    jobId: string;
  }) => ({
    "data-feature": "submit job application in forward",
    "data-property-source": args.source,
    "data-property-org-id": args.orgId,
    "data-property-job-id": args.jobId,
  }),
} as const;

export type FirmProfileIngressSource =
  | "dashboard event card"
  | "job board job card"
  | "job board firm carousel card"
  | "job detail page"
  | "firm directory firm card"
  | "firm directory firm carousel card"
  | "job applications job card"
  | "job activity page"
  | "event directory event card"
  | "event directory firm carousel card"
  | "event directory quick register end"
  | "event detail page"
  | "quick apply end"
  | "unknown";

type FirmProfileViewDetailedBreakdownType =
  | "summer program"
  | "diversity and inclusion"
  | "practice areas"
  | "pro bono";

type FirmDirectoryIngressSource =
  | "nav bar"
  | "dashboard banner"
  | "event directory empty state";

export type ForwardJobApplicationSource =
  | "job board"
  | "firm profile job carousel"
  | "job detail page"
  | "unknown";

type ForwardPublishEventSource =
  // modal in v1 app
  | "modal got it"
  // publishing page in v2 app
  | "publish button";

type ForwardUnpublishEventSource = "unpublish button";

type StartRegisterForFirmEventInForwardSource =
  | "event directory event card"
  | "event detail page event card"
  | "firm profile event carousel event card"
  // shouldn't happen
  | "unknown";

type FirmEventDetailPageIngressSource =
  | "event directory event card"
  | "firm profile event carousel event card"
  | "dashboard event card"
  // shouldn't happen
  | "unknown";

/**
 * TODO: Add sources in the future if we put this link in different places
 */
type VisitFirmWebsiteSource = "firm profile header";

// TODO: Allow this to accept receive props passed down
// https://www.radix-ui.com/primitives/docs/guides/composition#your-component-must-spread-props
type FirmProfileLinkProps = {
  source: FirmProfileIngressSource;
  children: React.ReactNode;
  index?: number;
  shouldOpenInNewTab?: boolean;
  className?: string;
  /**
   * Makes it easier to conditionally link to firm profile when wrapping other components in this component.
   */
  shouldLink?: boolean;
} & FirmProfileLinkInfoFragment;
export function FirmProfileLink(props: FirmProfileLinkProps) {
  if (
    !shouldFirmProfileLinkBeActive({
      hasPublicFirmProfile: props.hasPublicFirmProfile,
      urlFriendlyName: props.urlFriendlyName,
    }) ||
    // help TS infer props.urlFriendlyName
    !props.urlFriendlyName ||
    props.shouldLink === false
  ) {
    return <>{props.children}</>;
  }

  const href = URLS.forward.discovery.firmProfile(props.urlFriendlyName);
  const shouldOpenInNewTabAttributes = props.shouldOpenInNewTab
    ? openInNewTabHtmlAttributes
    : {};

  return (
    <Link href={href} passHref>
      <a
        href={href}
        {...DATA_FEATURE_PROPERTIES.firmProfileIngress({
          source: props.source,
          orgId: props.id,
          orgName: props.name ?? "Unknown firm",
          index: props.index,
        })}
        className={props.className}
        aria-label={`Firm profile for ${props.name ?? "Unknown firm"}`}
        {...shouldOpenInNewTabAttributes}
      >
        {props.children}
      </a>
    </Link>
  );
}

export function shouldFirmProfileLinkBeActive(args: {
  hasPublicFirmProfile: boolean;
  urlFriendlyName?: string | null;
}) {
  return args.hasPublicFirmProfile && !!args.urlFriendlyName;
}

type ViewDetailedBreakdownButtonProps = {
  onClick: () => void;
  type: FirmProfileViewDetailedBreakdownType;
  orgId: string;
  orgName: string;
};
export function ViewDetailedBreakdownButton(
  props: ViewDetailedBreakdownButtonProps,
) {
  return (
    <Button
      onClick={props.onClick}
      color="outline"
      {...DATA_FEATURE_PROPERTIES.firmProfileViewDetailedBreakdownType({
        type: props.type,
        orgId: props.orgId,
        orgName: props.orgName,
      })}
      UNSAFE_className="whitespace-nowrap"
    >
      <span className="flex flex-row items-center text-sm font-semibold">
        <FiLayout className="mr-2" width={16} height={16} />
        <p className="hidden lg:block">View Detailed Breakdown</p>
        <p className="block lg:hidden">Details</p>
      </span>
    </Button>
  );
}
